import { DOCUMENT } from '@angular/common';
import { Injectable, computed, inject, signal } from '@angular/core';
import {
  DEFAULT_PAGE_SIZE,
  PAGE_SIZE_OPTIONS,
  USER_PREFS_STORAGE_KEY,
  clearPageSizeOverrides,
} from '../../shared/utils/filters';

/** Densidade das listagens: preferência GLOBAL do usuário, não configuração por tela. */
export type GridDensity = 'normal' | 'compact';

export interface UserPreferences {
  density: GridDensity;
  /** Itens por página, padrão de TODAS as listagens (o rodapé de cada uma pode abrir exceção). */
  pageSize: number;
}

const STORAGE_KEY = USER_PREFS_STORAGE_KEY;

// Compacta é o PADRÃO do sistema: as listagens de operação são lidas o dia inteiro e cabe mais
// linha por tela sem rolar. Quem preferir respiro troca no menu do usuário e a escolha persiste.
const DEFAULTS: UserPreferences = { density: 'compact', pageSize: DEFAULT_PAGE_SIZE };

/**
 * Preferências de exibição do usuário — valem em TODO o sistema (abertas pelo menu do usuário,
 * não por grid). Ficam no navegador porque descrevem conforto visual da máquina em uso, não
 * dado de negócio: o mesmo usuário quer compacto no notebook e normal na TV da operação.
 *
 * A densidade é aplicada num atributo no <body> (`data-density`) e o CSS global de tabela lê
 * dali — assim nenhuma listagem precisa saber que a preferência existe.
 */
@Injectable({ providedIn: 'root' })
export class UserPreferencesService {
  private readonly document = inject(DOCUMENT);
  private readonly state = signal<UserPreferences>(this.restore());

  public readonly preferences = this.state.asReadonly();
  public readonly density = computed(() => this.state().density);
  public readonly pageSize = computed(() => this.state().pageSize);
  public readonly pageSizeOptions = PAGE_SIZE_OPTIONS;

  constructor() {
    this.applyDensity(this.state().density);
  }

  public setDensity(density: GridDensity): void {
    if (density === this.state().density) return;
    this.patch({ density });
    this.applyDensity(density);
  }

  /**
   * Define o padrão e APAGA as exceções por tela: quem muda a global espera ver a mudança em toda
   * listagem já na próxima visita. Uma troca no rodapé depois disso volta a ser exceção da tela.
   * As listagens abertas seguem com o tamanho atual até recarregar — o rodapé não assina isto.
   */
  public setPageSize(size: number): void {
    if (!PAGE_SIZE_OPTIONS.includes(size) || size === this.state().pageSize) return;
    this.patch({ pageSize: size });
    clearPageSizeOverrides();
  }

  private patch(changes: Partial<UserPreferences>): void {
    const next = { ...this.state(), ...changes };
    this.state.set(next);
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(next));
    } catch {
      // Modo privado / cota cheia: a preferência vale só nesta sessão, sem quebrar a tela.
    }
  }

  private applyDensity(density: GridDensity): void {
    const body = this.document.body;
    if (!body) return;
    if (density === 'normal') body.removeAttribute('data-density');
    else body.setAttribute('data-density', density);
  }

  private restore(): UserPreferences {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      if (!raw) return { ...DEFAULTS };
      const parsed = JSON.parse(raw) as Partial<UserPreferences> | null;
      return {
        // Valor salvo VENCE o padrão nos dois sentidos: com o padrão compacto, cair no default
        // quando o storage diz 'normal' desfaria a escolha explícita do usuário a cada carga.
        density: parsed?.density === 'compact' || parsed?.density === 'normal'
          ? parsed.density
          : DEFAULTS.density,
        pageSize: PAGE_SIZE_OPTIONS.includes(Number(parsed?.pageSize))
          ? Number(parsed?.pageSize)
          : DEFAULTS.pageSize,
      };
    } catch {
      return { ...DEFAULTS };
    }
  }
}
